// src/context/DataContext.js
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { getProductByCode } from "../constants/products";
import { getColorsByProductCode } from "../constants/colors";
import { getDefaultLineValues } from "../utils/lineDefaults";
import { v4 as uuidv4 } from "uuid";

const DataContext = createContext();

export const DataProvider = ({ children, showSnackbar = () => {} }) => {
  const lineIdRef = useRef(0);

  const [state, setState] = useState({
    selectedProduct: null,
    productCode: null,
    placeholderRef: null,
    lines: [],
    quantity: 1,
    mivaGlobals: null,
    backgroundImage: null,
    lockBackgroundImage: false,
  });

  const [loading, setLoading] = useState(false);

  const setSnackbar = useCallback(
    (config) => {
      if (config?.open === false) {
        showSnackbar("", "info");
      } else {
        showSnackbar(config.message, config.severity || "info");
      }
    },
    [showSnackbar]
  );

  /* ────────────────────── SETTERS ────────────────────── */
  const setPlaceholderRef = (r) =>
    setState((s) => ({ ...s, placeholderRef: r }));
  const setQuantity = (q) =>
    setState((s) => ({ ...s, quantity: Math.max(1, q) }));
  const setMivaGlobals = (g) => setState((s) => ({ ...s, mivaGlobals: g }));
  const setBackgroundImage = (url) =>
    setState((s) => ({ ...s, backgroundImage: url }));
  const setLockBackgroundImage = (lock) =>
    setState((s) => ({ ...s, lockBackgroundImage: lock }));

  /* ────────────────────── PRODUCT ────────────────────── */
  const setSelectedProduct = (product) => {
    if (!product) return;

    setState((s) => ({
      ...s,
      selectedProduct: product,
      productCode: product.code,
      lines: [],
      backgroundImage: null,
      lockBackgroundImage: false,
    }));
  };

  /* ────────────────────── LINES ────────────────────── */
  const updateLine = useCallback((id, updates) => {
    setState((s) => ({
      ...s,
      lines: s.lines.map((l) => (l.id === id ? { ...l, ...updates } : l)),
    }));
  }, []);

  const addLine = useCallback(() => {
    if (state.lines.length >= 5) {
      setSnackbar({
        open: true,
        message: "Maximum 5 lines allowed",
        severity: "warning",
      });
      return;
    }
    const defaults = getDefaultLineValues(state.selectedProduct);

    const newLine = {
      id: uuidv4(),
      canvasObjId: `text_${uuidv4()}`,
      version: 0,
      ...defaults,
      text: "",
      widthInches: 0,
      heightInches: 0,
    };

    setState((s) => ({ ...s, lines: [...s.lines, newLine] }));
  }, [state.selectedProduct, state.lines.length, setSnackbar]);

  const removeLine = useCallback((id) => {
    setState((s) => ({
      ...s,
      lines: s.lines.filter((l) => l.id !== id),
    }));
  }, []);

  const setCanvasObj = useCallback((lineId, canvasObj) => {
    setState((s) => ({
      ...s,
      lines: s.lines.map((l) => (l.id === lineId ? { ...l, canvasObj } : l)),
    }));
  }, []);

  // MIGRATION + AUTO-ADD FIRST LINE (unchanged)
  useEffect(() => {
    if (!state.selectedProduct || state.lines.length === 0) return;

    const defaults = getDefaultLineValues(state.selectedProduct);
    const colors = getColorsByProductCode(state.selectedProduct.code) || [];

    setState((s) => ({
      ...s,
      lines: s.lines.map((line) => {
        const hasValidProps =
          line.size &&
          line.sizeName &&
          line.color &&
          line.colorName &&
          line.font;

        if (hasValidProps) return line;

        let updatedLine = { ...line };

        if (!line.color && line.colorName && colors.length > 0) {
          const matchedColor = colors.find(
            (c) => c.name === line.colorName || c.mivaname === line.colorName
          );
          if (matchedColor) {
            updatedLine.color = matchedColor.mivaname || matchedColor.code;
            updatedLine.colorName = matchedColor.name;
          }
        }

        return {
          ...defaults,
          ...updatedLine,
          text: line.text || "",
          canvasObj: line.canvasObj || null,
        };
      }),
    }));
  }, [state.selectedProduct, state.lines.length]);

  useEffect(() => {
    if (state.selectedProduct && state.lines.length === 0) {
      addLine();
    }
  }, [state.selectedProduct, state.lines.length, addLine]);

  const reset = () =>
    setState({
      selectedProduct: null,
      productCode: null,
      placeholderRef: null,
      lines: [],
      quantity: 1,
      snackbar: { open: false, message: "", severity: "info" },
      mivaGlobals: null,
      backgroundImage: null,
      lockBackgroundImage: false,
    });

  const basketItem = useMemo(() => {
    if (!state.selectedProduct) return { productCode: "", lines: [] };

    return {
      productCode: state.selectedProduct.code,
      lines: state.lines
        .filter((line) => line.text?.trim())
        .map((line) => ({
          id: line.id,
          phrase: line.text?.trim() || "",
          font: line.font || "",
          color: line.color || "",
          finish: line.finish || "",
          thickness: line.thickness || "",
          size: line.size || "",
          sizeName: line.sizeName || "",
          colorName: line.colorName || "",
          hardware: line.hardware || "",
          price: Number(line.price) || 0,
          widthInches: Number(line.widthInches || 0).toFixed(2),
          heightInches: Number(line.heightInches || 0).toFixed(2),
        })),
    };
  }, [state.selectedProduct, state.lines]);


  const value = {
    ...state,
    basketItem,
    setPlaceholderRef,
    setSnackbar,
    setQuantity,
    setMivaGlobals,
    setSelectedProduct,
    updateLine,
    addLine,
    removeLine,
    reset,
    loading,
    setLoading,
    setBackgroundImage,
    setLockBackgroundImage,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

export const useData = () => {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error("useData must be used inside DataProvider");
  return ctx;
};
